import React, { useEffect, useState } from 'react';
import { Check, X, Sparkles } from 'lucide-react';
import { proficiencyBadgeClass, proficiencyLabel, getSkillIconMeta, SKILL_ICON_IMG_CLASS } from './skillsConstants';

/**
 * A single derived/declared skill tag badge.
 * When `onReview` is provided, confirm/reject controls are shown (self-edit mode).
 */
export default function SkillTag({ tag, onReview, reviewing = false }: any) {
  const badge = proficiencyBadgeClass(tag.proficiencySignal);
  const confirmed = tag.userReview === 'confirmed';
  const isDeclared = (tag.evidenceSources || []).includes('declaration');
  const iconMeta = getSkillIconMeta(tag.name, tag.tagType);
  const iconUrl = iconMeta?.url ?? null;
  const [imgFailed, setImgFailed] = useState(false);

  useEffect(() => {
    setImgFailed(false);
  }, [iconUrl]);

  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1 text-sm font-medium ${badge} ${
        confirmed ? 'ring-1 ring-inset ring-current/40' : ''
      }`}
      title={`${proficiencyLabel(tag.proficiencySignal)}${
        tag.evidenceScore != null ? ` · evidence ${Math.round(tag.evidenceScore * 100)}%` : ''
      }${tag.repoCount ? ` · ${tag.repoCount} repo${tag.repoCount === 1 ? '' : 's'}` : ''}`}
    >
      {iconUrl && !imgFailed ? (
        <img
          src={iconUrl}
          alt=""
          aria-hidden
          loading="lazy"
          className={`h-3.5 w-3.5 flex-shrink-0 object-contain ${SKILL_ICON_IMG_CLASS}`}
          onError={() => setImgFailed(true)}
        />
      ) : null}
      {isDeclared && <Sparkles className="w-3 h-3 flex-shrink-0 opacity-80" aria-hidden />}
      <span className="truncate max-w-[14rem]">{tag.name}</span>
      <span className="text-[11px] opacity-80">{proficiencyLabel(tag.proficiencySignal)}</span>
      {onReview && (
        <span className="ml-0.5 inline-flex items-center gap-0.5">
          <button
            type="button"
            disabled={reviewing}
            onClick={() => onReview(tag, confirmed ? null : 'confirmed')}
            className={`rounded p-0.5 hover:bg-white/10 disabled:opacity-40 ${confirmed ? 'text-vivid-green-cyan' : ''}`}
            aria-label={confirmed ? `Unconfirm ${tag.name}` : `Confirm ${tag.name}`}
            title={confirmed ? 'Confirmed — click to undo' : 'Confirm this skill'}
          >
            <Check className="w-3.5 h-3.5" />
          </button>
          <button
            type="button"
            disabled={reviewing}
            onClick={() => onReview(tag, 'rejected')}
            className="rounded p-0.5 hover:bg-white/10 disabled:opacity-40"
            aria-label={`Reject ${tag.name}`}
            title="Reject — hide this skill from your profile"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </span>
      )}
    </span>
  );
}
